import React, { useState } from "react";
import { Form, Input, Button, Spin, Row, Col, TimePicker, Modal } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import { TIME_FORMAT_FRONTEND } from "../../../constants";
import AddressInput from "../../Inputs/AddressInput";
import { getTranslation, setCountryCookie } from "../../../utils/helper";
import DefaultContactLocation from "./DefaultContactLocation";
import dayjs from "dayjs";

const DeliveryForm = ({ form, isFetching, handleSubmit, handleCancel, countryCodeInit }) => {
  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  const [countryPhone, setCountryPhone] = useState();
  const [state, setState] = useState({
    visibleModal: false,
    contactType: null,
    contact: null,
  });

  const handleOnFinish = values => {
    const params = { ...values };
    params.deliveryPhone =
      !isEmpty(values.deliveryPhone) && countryPhone?.dialCode !== values.deliveryPhone
        ? `${values.deliveryPhone.includes("+") ? "" : "+"}${values.deliveryPhone}`
        : null;
    handleSubmit(params);
  };

  const handleOnChangeDeliveryFrom = value => {
    form.setFieldsValue({ deliveryTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeEnd"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeEnd") < value) {
      form.setFields([
        {
          name: "deliveryTimeStart",
          errors: ["Delivery From must be less than Delivery To"],
        },
      ]);
    }
  };

  const handleOnChangeDeliveryTo = value => {
    form.setFieldsValue({ deliveryTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("deliveryTimeStart"))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue("deliveryTimeStart") > value) {
      form.setFields([
        {
          name: "deliveryTimeEnd",
          errors: ["Delivery To must be greater than Delivery From"],
        },
      ]);
    }
  };

  const resetErrorDeliveryTime = () => {
    form.setFields([
      {
        name: "deliveryTimeStart",
        errors: [],
      },
      {
        name: "deliveryTimeEnd",
        errors: [],
      },
    ]);
  };

  const handleUseDefaultLocation = () => {
    setState({
      ...state,
      visibleModal: true,
      contactType: "DELIVERY",
      contact: null,
    });
  };

  const handleSubmitSelectContact = () => {
    if (state.contact) {
      form.setFieldsValue({
        deliveryName: state.contact.name,
        deliveryPhone: state.contact.phone,
        deliveryTimeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
        deliveryTimeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
        deliveryDuration: state.contact.duration,
        deliveryAddress: {
          address: state.contact.address,
          latLng: {
            lat: state.contact.lat,
            lng: state.contact.lng,
          },
        },
      });
    }
    handleCancelSelectContact();
  };

  const handleCancelSelectContact = () => {
    setState({ ...state, visibleModal: false, contact: null });
  };

  return (
    <Spin spinning={isFetching}>
      <div className="DeliveryForm">
        <Form form={form} scrollToFirstError layout="vertical" hideRequiredMark colon={false} onFinish={handleOnFinish}>
          <div className="FormHeader">
            <div className="left">{getTranslation(setting, "Orders", "Edit Delivery")}</div>
            <div className="right">
              <Link to="#" onClick={handleUseDefaultLocation}>
                {getTranslation(setting, "Orders", "Add from Contact List")}
                <ReactSVG className="icon" src="/icons/default-location.svg" />
              </Link>
            </div>
          </div>
          <div className="FormContent">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={getTranslation(setting, "Orders", "Delivery Name")} name="deliveryName">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Name")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={getTranslation(setting, "Orders", "Delivery Phone Number")} name="deliveryPhone">
                  <PhoneInput
                    country={countryCodeInit}
                    enableSearch
                    disableSearchIcon
                    onChange={(value, country) => {
                      setCountryPhone(country);
                      setCountryCookie(value, country, user);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Delivery Email")}
                  name="deliveryEmail"
                  rules={[{ type: "email", message: "Invalid Email" }]}>
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Email")} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={getTranslation(setting, "Orders", "Delivery From")} name="deliveryTimeStart">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery From")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeDeliveryFrom}
                    onCalendarChange={handleOnChangeDeliveryFrom}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={getTranslation(setting, "Orders", "Delivery To")} name="deliveryTimeEnd">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery To")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeDeliveryTo}
                    onCalendarChange={handleOnChangeDeliveryTo}
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Delivery Address")}
                  name="deliveryAddress"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                          return Promise.resolve();
                        }

                        if (isEmpty(value?.address)) {
                          return Promise.reject(new Error("Required"));
                        }

                        return Promise.reject(new Error("Invalid"));
                      },
                    },
                  ]}>
                  <AddressInput
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Delivery Address")}
                    onSelect={value => form.setFieldsValue({ deliveryAddress: value })}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Delivery Duration (minutes)")}
                  name="deliveryDuration">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Delivery Duration (minutes)")} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Button key="cancel" onClick={handleCancel}>
                {getTranslation(setting, "Common", "Cancel")}
              </Button>
              <Button htmlType="submit" type="primary">
                {getTranslation(setting, "Common", "Update")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </div>
      <Modal open={state.visibleModal} onCancel={handleCancelSelectContact} footer={[]} width={792}>
        <DefaultContactLocation
          state={state}
          setState={setState}
          handleCancel={handleCancelSelectContact}
          handleSubmit={handleSubmitSelectContact}
        />
      </Modal>
    </Spin>
  );
};

export default DeliveryForm;
